import { Cpu, Gamepad2, Joystick, Trophy, Layers, Sparkles, Save, Download, Github, Rocket, ArrowRight, Star, Bug, PlayCircle, Wand2, Share2, GitBranch } from "lucide-react";
import CodeBlock from "./CodeBlock";

const ENGINE_TONE = {
  SpriteKit: "bg-saffron/15 text-marigold",
  SceneKit: "bg-teal/15 text-teal",
  RealityKit: "bg-ink/10 text-ink",
};

const ENGINES = ["SpriteKit", "SceneKit", "RealityKit"];

export default function GameBlueprintCard({ bp, onSave, saving, saved, onExport, onPushGitHub, onOpenSceneEditor, onOpenDebug, onEngineChange, onPreview, onBridge, onShare, onBranch }) {
  if (!bp) return null;
  const engineTone = ENGINE_TONE[bp.engine] || "bg-paper text-ink2";

  return (
    <article className="space-y-6" data-testid="game-blueprint-card">
      {/* Header */}
      <header className="card-soft p-6 md:p-8 bg-gradient-to-br from-cream via-paper to-marigold/10">
        <div className="flex flex-wrap items-start gap-3 mb-3">
          {/* Engine selector — click to override the AI's pick */}
          {onEngineChange ? (
            <div className="inline-flex rounded-full border-2 border-ink p-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="game-engine-selector">
              {ENGINES.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => onEngineChange(e)}
                  className={`px-2.5 py-1 rounded-full transition flex items-center gap-1 ${bp.engine === e ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
                  data-testid={`game-engine-pick-${e}`}
                >
                  {bp.engine === e && <Cpu className="w-3 h-3" />} {e}
                </button>
              ))}
            </div>
          ) : (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${engineTone}`} data-testid="game-engine-badge">
              <Cpu className="w-3 h-3" /> {bp.engine || "SpriteKit"}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ink/5 text-ink2 text-xs font-medium" data-testid="game-genre-badge">
            <Gamepad2 className="w-3 h-3" /> {bp.genre || "Game"}
          </span>
          {bp.session_length && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ink/5 text-ink2 text-xs font-medium">
              ⏱ {bp.session_length}
            </span>
          )}
          {(bp.target_devices || []).slice(0, 4).map((d) => (
            <span key={d} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-paper border border-border text-ink2 font-medium">{d}</span>
          ))}
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold leading-tight mb-2">{bp.title}</h2>
        <p className="text-ink2 leading-relaxed max-w-3xl">{bp.concept}</p>

        <div className="flex flex-wrap gap-2 mt-5">
          {onPreview && (
            <button onClick={onPreview} className="btn-primary text-sm py-2" data-testid="game-preview-browser-button">
              <PlayCircle className="w-4 h-4" /> Preview in Browser
            </button>
          )}
          {onBridge && (
            <button onClick={onBridge} className="btn-outline-ink text-sm py-2 relative" data-testid="game-bridge-browser-button">
              <Wand2 className="w-4 h-4" /> Bridge from Swift
              <span className="absolute -top-1.5 -right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-marigold text-cream tracking-wider">AI</span>
            </button>
          )}
          {onExport && (
            <button onClick={onExport} className="btn-outline-ink text-sm py-2" data-testid="game-export-button">
              <Download className="w-4 h-4" /> Download starter
            </button>
          )}
          {onOpenSceneEditor && (
            <button onClick={onOpenSceneEditor} className="btn-outline-ink text-sm py-2" data-testid="game-scene-editor-button">
              <Layers className="w-4 h-4" /> Visual Scene Editor
            </button>
          )}
          {onOpenDebug && (
            <button onClick={onOpenDebug} className="btn-outline-ink text-sm py-2" data-testid="game-debug-card-button">
              <Bug className="w-4 h-4" /> Debug Assistant
            </button>
          )}
          {onPushGitHub && (
            <button onClick={onPushGitHub} className="btn-outline-ink text-sm py-2" data-testid="game-push-github-button">
              <Github className="w-4 h-4" /> Push to GitHub
            </button>
          )}
          {onSave && (
            <button
              onClick={onSave}
              disabled={saving || saved}
              className="btn-outline-ink text-sm py-2 disabled:opacity-40"
              data-testid="game-save-button"
            >
              <Save className="w-4 h-4" /> {saved ? "Saved" : (saving ? "Saving…" : "Save")}
            </button>
          )}
          {onShare && (
            <button onClick={onShare} className="btn-outline-ink text-sm py-2" data-testid="game-share-button">
              <Share2 className="w-4 h-4" /> Share preview link
            </button>
          )}
          {onBranch && (
            <button onClick={onBranch} className="btn-outline-ink text-sm py-2" data-testid="game-branch-button">
              <GitBranch className="w-4 h-4" /> Branch
            </button>
          )}
        </div>
      </header>

      {/* Gameplay loop */}
      {(bp.gameplay_loop || []).length > 0 && (
        <Section icon={Joystick} title="Gameplay loop" testid="game-loop">
          <ol className="space-y-2 mt-2">
            {bp.gameplay_loop.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="w-7 h-7 rounded-full bg-marigold/15 text-marigold font-mono font-bold text-xs flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-ink2 leading-relaxed pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Mechanics + Scoring grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {(bp.player_mechanics || []).length > 0 && (
          <Section icon={Sparkles} title="Player mechanics" testid="game-mechanics" inline>
            <ul className="space-y-2 mt-2">
              {bp.player_mechanics.map((m, i) => (
                <li key={i} className="text-sm">
                  <span className="font-mono font-bold text-ink">{m.name}</span>
                  <span className="text-ink2"> — {m.description}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {bp.scoring && (
          <Section icon={Trophy} title="Scoring" testid="game-scoring" inline>
            <ul className="space-y-1.5 text-sm mt-2">
              {bp.scoring.primary && <li><span className="text-ink2">Primary:</span> <span className="font-medium text-ink">{bp.scoring.primary}</span></li>}
              {(bp.scoring.bonuses || []).length > 0 && (
                <li><span className="text-ink2">Bonuses:</span> <span className="font-medium text-ink">{(bp.scoring.bonuses || []).join(", ")}</span></li>
              )}
              {bp.scoring.win_condition && <li><span className="text-ink2">Win:</span> <span className="font-medium text-teal">{bp.scoring.win_condition}</span></li>}
              {bp.scoring.lose_condition && <li><span className="text-ink2">Lose:</span> <span className="font-medium text-marigold">{bp.scoring.lose_condition}</span></li>}
            </ul>
          </Section>
        )}
      </div>

      {/* Levels */}
      {(bp.level_design || []).length > 0 && (
        <Section icon={Layers} title="Levels" testid="game-levels">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
            {bp.level_design.map((lvl, i) => (
              <div key={i} className="card-soft p-3 text-sm">
                <div className="font-heading font-semibold text-ink mb-1">{lvl.name}</div>
                <div className="text-xs text-ink2 mb-1">{lvl.theme}</div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-paper text-ink2 font-medium border border-border">
                  {lvl.difficulty}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Systems + scene tree */}
      <div className="grid md:grid-cols-2 gap-4">
        {(bp.systems || []).length > 0 && (
          <Section icon={Cpu} title="Game systems" testid="game-systems" inline>
            <ul className="space-y-2 mt-2 text-sm">
              {bp.systems.map((s, i) => (
                <li key={i}>
                  <span className="font-mono text-ink font-bold">{s.name}</span>
                  <span className="text-ink2"> — {s.purpose}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {(bp.scene_tree || []).length > 0 && (
          <Section icon={Layers} title="Scene tree" testid="game-scene-tree" inline>
            <ul className="space-y-2 mt-2 text-sm font-mono">
              {bp.scene_tree.map((sc, i) => (
                <li key={i}>
                  <span className="text-ink font-bold">{sc.name}</span>
                  <ul className="pl-4 space-y-0.5 text-xs text-ink2 mt-1">
                    {(sc.nodes || []).map((n, j) => (
                      <li key={j}>· {n}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {/* Starter Swift code */}
      {bp.starter_code && (
        <Section icon={Sparkles} title="Starter SpriteKit code" testid="game-starter-code">
          <CodeBlock code={bp.starter_code} filename="GameScene.swift" />
        </Section>
      )}

      {bp.swiftui_shell && (
        <Section icon={Sparkles} title="SwiftUI shell" testid="game-shell-code">
          <CodeBlock code={bp.swiftui_shell} filename="ContentView.swift" />
        </Section>
      )}

      {/* App Store assets */}
      {(bp.app_store_description || (bp.app_store_keywords || []).length > 0 || bp.monetization) && (
        <Section icon={Star} title="App Store assets" testid="game-appstore">
          {bp.app_store_description && <p className="text-sm text-ink2 leading-relaxed mt-2">{bp.app_store_description}</p>}
          {(bp.app_store_keywords || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {bp.app_store_keywords.map((k, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded bg-paper border border-border text-ink2 font-mono">{k}</span>
              ))}
            </div>
          )}
          {bp.monetization && (
            <div className="text-sm mt-3">
              <span className="text-ink2">Monetization:</span> <span className="font-medium text-ink">{bp.monetization}</span>
            </div>
          )}
        </Section>
      )}

      {/* Next Steps */}
      <NextSteps onExport={onExport} onPushGitHub={onPushGitHub} onSave={onSave} saved={saved} />
    </article>
  );
}

function Section({ icon: Icon, title, children, testid, inline }) {
  return (
    <section className={`card-soft ${inline ? "p-5" : "p-6 md:p-7"}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-marigold">
        <Icon className="w-4 h-4" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function NextSteps({ onExport, onPushGitHub, onSave, saved }) {
  const steps = [
    onExport && { n: 1, title: "Download the Xcode starter", desc: "Open in Xcode 15+, hit ⌘R, watch your game scene run.", action: "Download .zip", onClick: onExport, tone: "bg-saffron/15 text-marigold", testid: "game-next-step-download" },
    onPushGitHub && { n: 2, title: "Push to GitHub", desc: "One click — bundled Actions workflow auto-builds and uploads to TestFlight on every tag.", action: "Create repo & push", onClick: onPushGitHub, tone: "bg-ink/10 text-ink", testid: "game-next-step-push" },
    onSave && !saved && { n: 3, title: "Save to your dashboard", desc: "Keep this blueprint, iterate on it, and export again as you tune mechanics.", action: "Save", onClick: onSave, tone: "bg-marigold/15 text-marigold", testid: "game-next-step-save" },
  ].filter(Boolean);
  if (!steps.length) return null;
  return (
    <div className="card-soft p-6 md:p-7 bg-gradient-to-br from-cream via-paper to-saffron/10" data-testid="game-next-steps">
      <div className="flex items-center gap-2 mb-5 text-marigold">
        <Rocket className="w-5 h-5" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink">Next steps</h3>
        <span className="text-xs text-ink2 ml-2 font-normal normal-case tracking-normal">From prompt to TestFlight, in order.</span>
      </div>
      <ol className="space-y-3">
        {steps.map((s) => (
          <li key={s.testid} className="flex flex-wrap items-start gap-3">
            <span className={`w-9 h-9 rounded-full ${s.tone} flex items-center justify-center font-mono text-sm font-bold flex-shrink-0`}>
              {s.n}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink">{s.title}</div>
              <div className="text-sm text-ink2 leading-relaxed">{s.desc}</div>
            </div>
            <button
              onClick={s.onClick}
              data-testid={s.testid}
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition flex-shrink-0"
            >
              {s.action} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
